import { useEffect, useRef } from 'react';
import type { ExtractedEvent } from '../types';

interface Props {
  /** The event being approved. When `null`, the modal is hidden. */
  event: ExtractedEvent | null;
  onClose: () => void;
  /**
   * Confirm handler. `flagForAiTraining=true` means the admin wants this
   * approval added to the AI training review queue (i.e. the pipeline
   * should have been confident enough to auto-publish without the queue).
   */
  onConfirm: (event: ExtractedEvent, flagForAiTraining: boolean) => void;
}

/**
 * In-app confirmation modal for approving an event from the review queue.
 * Mirrors `RejectEventModal` so the approve / reject UX is symmetric.
 *
 * Two confirm actions:
 *   1. **Approve only** — marks the event `status='approved'` (current behavior).
 *   2. **Approve & flag for AI auto-approval** — same approval, plus records
 *      `flaggedForAiTraining=true` on the decision so we can review the
 *      auto-publish confidence threshold / extractor prompts.
 */
export default function ApproveEventModal({ event, onClose, onConfirm }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!event) return;
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [event, onClose]);

  if (!event) return null;
  const label = event.title || 'this event';

  return (
    <div
      className="mh-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mh-approve-modal-title"
      onClick={onClose}
    >
      <div
        className="mh-modal-dialog mh-modal-dialog--sm"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="d-flex align-items-start gap-2 p-3 border-bottom">
          <h2 id="mh-approve-modal-title" className="h5 mb-0 flex-grow-1">
            Approve event
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </header>
        <div className="p-3">
          <p className="mb-2">
            Approve <strong>&ldquo;{label}&rdquo;</strong>? It will be
            published to the public list.
          </p>
          <p className="text-muted small mb-0">
            <strong>Approve &amp; flag for AI auto-approval</strong> also
            adds it to the AI training review queue (Ops &rarr; AI training)
            so we can tune the auto-publish confidence threshold and
            extractor prompts to skip the queue for events like this.
          </p>
        </div>
        <footer className="d-flex flex-wrap justify-content-end gap-2 p-3 border-top">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={() => onConfirm(event, false)}
          >
            <i className="bi bi-check2-circle me-1" aria-hidden="true" />
            Approve only
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={() => onConfirm(event, true)}
          >
            <i className="bi bi-robot me-1" aria-hidden="true" />
            Approve &amp; flag for AI auto-approval
          </button>
        </footer>
      </div>
    </div>
  );
}
